/**
 * CRM 2.0 — US-003 RBAC Frontend
 *
 * PermissionsContext: loads the authenticated admin's permissions once from
 * GET /v2/admin/me and makes them available app-wide via usePermissions().
 *
 * Deliberately lightweight — no external dependency (no CASL, no rbac-lib).
 * The permission list is a simple string[] fetched once per session and stored
 * in React state. Re-fetched after login and cleared on logout.
 *
 * Usage:
 *   const { hasPermission } = usePermissions();
 *   hasPermission('users.read') // → boolean
 *
 * Gating shorthand: use the <Can> component (src/components/auth/Can.tsx).
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiV2 } from '../lib/api';
import { useAuth } from './AuthContext';

interface AdminMeData {
  id: number;
  name: string;
  email: string;
  locale: string;
  role: { id: number; name: string } | null;
  permissions: string[];
  // CRM 2.0 / feature/crm2-3cx-integration — 3CX extension (null when unset).
  three_cx_extension?: string | null;
}

interface PermissionsContextValue {
  /** Sorted permission keys for the authenticated admin. Empty when not loaded. */
  permissions: string[];
  /** Current role info (may be null if admin has no role). */
  role: AdminMeData['role'];
  /** True while the initial /me fetch is in flight. */
  loading: boolean;
  /** True if /me returned an error. */
  error: boolean;
  /** Check whether the admin holds a specific permission key. */
  hasPermission: (key: string) => boolean;
  /** Force a re-fetch (e.g. after role assignment change or after saving the 3CX extension). */
  refresh: () => void;
  /**
   * CRM 2.0 / feature/crm2-3cx-integration — the admin's 3CX extension, if
   * set in Profil-Einstellungen. null while unset/not yet loaded — CallPopup
   * only starts polling /admin/3cx/active-call once this is a non-empty string.
   */
  threeCxExtension: string | null;
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token } = useAuth();

  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole]               = useState<AdminMeData['role']>(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(false);
  const [threeCxExtension, setThreeCxExtension] = useState<string | null>(null);
  // Track which token we last fetched for so we don't double-fetch
  const fetchedForToken = useRef<string | null>(null);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiV2.get<{ data: AdminMeData }>('/admin/me');
      const data = res.data.data;
      setPermissions(data.permissions ?? []);
      setRole(data.role ?? null);
      setThreeCxExtension(data.three_cx_extension ?? null);
    } catch {
      setError(true);
      setPermissions([]);
      setRole(null);
      setThreeCxExtension(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when the user authenticates (token changes from null → value)
  useEffect(() => {
    if (isAuthenticated && token && token !== fetchedForToken.current) {
      fetchedForToken.current = token;
      void fetchMe();
    }
    if (!isAuthenticated) {
      fetchedForToken.current = null;
      setPermissions([]);
      setRole(null);
      setThreeCxExtension(null);
      setError(false);
    }
  }, [isAuthenticated, token, fetchMe]);

  const hasPermission = useCallback(
    (key: string) => permissions.includes(key),
    [permissions],
  );

  const refresh = useCallback(() => {
    fetchedForToken.current = null;
    void fetchMe();
  }, [fetchMe]);

  return (
    <PermissionsContext.Provider value={{ permissions, role, loading, error, hasPermission, refresh, threeCxExtension }}>
      {children}
    </PermissionsContext.Provider>
  );
}

const SAFE_DEFAULTS: PermissionsContextValue = {
  permissions: [],
  role: null,
  loading: false,
  error: false,
  hasPermission: () => false,
  refresh: () => { /* noop */ },
  threeCxExtension: null,
};

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  // Return safe defaults when used outside the provider (e.g. tests rendering
  // Sidebar without the full provider tree). In production main.tsx always
  // wraps everything in PermissionsProvider.
  return ctx ?? SAFE_DEFAULTS;
}

/** Convenience single-key hook. */
export function usePermission(key: string): boolean {
  return usePermissions().hasPermission(key);
}
